import { QueryUpdatedEvent, ResultsUpdatedEvent } from '@theme/events';

/**
 * PredictiveSearch
 *
 * Handles Shopify-style predictive search UI, result fetching, and user interaction.
 * Manages search query updates, result panel rendering, resource (tab) switching, and accessibility.
 *
 * @module PredictiveSearch
 */
  

(function PredictiveSearch() {
  'use strict';

  /**
   * CSS selectors used throughout the PredictiveSearch module.
   * @constant {Object<string, string>}
   */
  const SELECTORS = {
    searchInput: '[data-search-input]',
    resultsContainer: '[data-predictive-search-container]',
    markup: '[data-predictive-search-markup]',
    searchTypeInput: '[data-search-type]',
    searchTypeChecked: '[data-search-type]:checked',
    resources: '[data-predictive-search-resources]',
    predictiveSearchLink: 'a[data-predictive-search-link]'
  };

  /**
   * Custom events emitted by PredictiveSearch.
   * @constant {Object<string, string>}
   */
  const EVENTS = {
    /** Event emitted when the search query changes. */
    queryUpdated: 'predictive:queryUpdated',
    /** Event emitted when the search results HTML is updated. 
     * Returns a custom PredictiveSearchResult https://shopify.dev/docs/api/storefront/latest/objects/PredictiveSearchResult
    */
    resultsUpdated: 'predictive:resultsUpdated',
  }

  // ----- Cached DOM elements -----
  /** @type {NodeListOf<HTMLInputElement>} */
  const searchInputs = document.querySelectorAll(SELECTORS.searchInput);
  /** @type {NodeListOf<HTMLElement>} */
  const resultsContainers = document.querySelectorAll(SELECTORS.resultsContainer);

  /**
   * The public API for PredictiveSearch.
   * @namespace
   */
  const publicAPI = {
    /** Resets the search results UI to initial/empty state. */
    resetResults,
    /** References to core elements. */
    elements: {
      searchInputs,
      resultsContainers
    },
    SELECTORS,
    EVENTS
  }

  window.POV = window.POV || {};
  /** @type {typeof publicAPI} */
  window.POV.PredictiveSearch = publicAPI;

  if (searchInputs.length === 0 || resultsContainers.length === 0) {
    // Stop if required elements are missing
    return;
  }

  // ----- Fetch control -----
  /** @type {AbortController|null} */
  let abortController = null;
  /** @type {number} */
  let requestCounter = 0; // Unique id for each input event

  // ----- Event listeners -----
  searchInputs.forEach(searchInput => {
    searchInput.addEventListener('input', window.POV.utils.debounce((event) => onSearchInput(event, searchInput), 128));
  });

  // =========================
  //        Functions
  // =========================

  /**
   * Handles search input events: triggers search, aborts old fetches, manages state.
   * @async
   * @param {Event} event - The input event.
   * @param {HTMLInputElement} searchInput - The specific search input element.
   * @returns {Promise<void>}
   */
  async function onSearchInput(event, searchInput) {
    const query = searchInput.value.trim();
    const searchInputAttribute = searchInput.getAttribute('data-search-input');
    const resultsContainer = Array.from(document.querySelectorAll(SELECTORS.resultsContainer))
      .find(container => container.getAttribute('data-predictive-search-container') === searchInputAttribute);

    if (!query) {
      resetResults(resultsContainer);
      abortOngoingFetch();
      return;
    }

    document.dispatchEvent(new QueryUpdatedEvent({
      "query": {
        text: query,
        __typename: 'CustomSearchQuery'
      }
    }));


    // Cancel any previous fetch
    abortOngoingFetch();

    // Prepare for new fetch
    abortController = new AbortController();
    const currentRequest = ++requestCounter;

    const searchUrl = buildSearchUrl(query);

    try {
      const response = await fetch(searchUrl, { signal: abortController.signal });
      if (!response.ok) throw new Error('Network response was not ok');
      const html = await response.text();

      // Only process if this is the latest input
      if (currentRequest === requestCounter) {
        updateResults(html, resultsContainer);
        setupContentTypeEvents(resultsContainer);
      }
    } catch (err) {
      if (err.name !== 'AbortError' && currentRequest === requestCounter) {
        // Optional: handle/display errors here
        // console.error('Predictive search error:', err);
      }
    } finally {
      if (currentRequest === requestCounter) abortController = null;
    }
  }

  /**
   * Aborts any ongoing predictive search fetch.
   */
  function abortOngoingFetch() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  /**
   * Constructs the predictive search URL for a given query.
   * @param {string} query - The search query string.
   * @returns {string} The URL to fetch predictive search results.
   */
  function buildSearchUrl(query) {
    const baseUrl = routes.predictive_search_url;
    const sectionHandle = document.querySelector('.predictive-search-section').id.replace('shopify-section-', '');
    const params = new URLSearchParams({
      q: query,
      section_id: sectionHandle,
      'resources[type]': 'product,query,article',
      'resources[limit_scope]': 'each'
    });
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Updates the results panel DOM with new predictive search results markup.
   * Emits {@link EVENTS.resultsUpdated}.
   * @param {string} html - The HTML string returned by the predictive search API.
   * @param {HTMLElement} resultsContainer - The specific results container to update.
   */
  function updateResults(html, resultsContainer) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const markup = doc.querySelector(SELECTORS.markup);

    if (markup) {
      // innerHTML copy drops attributes on the markup wrapper, so re-stamp the
      // term onto the live container for click / type-change attribution.
      resultsContainer.innerHTML = markup.innerHTML;
      resultsContainer.dataset.searchTerm = markup.dataset.searchTerm ?? '';
      resultsContainer.removeAttribute('aria-hidden');
    } else {
      resetResults(resultsContainer);
    }

    const trackingData = JSON.parse(markup.querySelector('[data-predictive-search-tracking]').innerHTML);
    document.dispatchEvent(new ResultsUpdatedEvent({ results: trackingData }));
  }

  /**
   * Resets the predictive search results UI to its initial state.
   * @param {HTMLElement|Event} [containerOrEvent] - The specific container to reset, or an event object if called as an event listener.
   */
  function resetResults(containerOrEvent) {
    const containersToReset = (containerOrEvent && containerOrEvent instanceof HTMLElement) 
      ? [containerOrEvent] 
      : Array.from(resultsContainers);

    containersToReset.forEach(container => {
      container.setAttribute('aria-hidden', 'true');
      container.innerHTML = '';
      container.removeAttribute('style');
      container.removeAttribute('data-search-term');
    });
  }

  /**
   * Attaches content-type change event listeners to filter/search-type inputs (e.g. product/article tabs).
   * Ensures that only the selected resource type is shown.
   * @param {HTMLElement} resultsContainer - The specific results container.
   */
  function setupContentTypeEvents(resultsContainer) {
    // To avoid duplicate listeners, always remove then add
    const searchTypeInputs = Array.from(resultsContainer.querySelectorAll(SELECTORS.searchTypeInput));
    searchTypeInputs.forEach(input => {
      input.removeEventListener('input', onContentTypeChange);
      input.addEventListener('input', onContentTypeChange);
    });
  }

  /**
   * Handles user changing the predictive search resource type (e.g., tabs).
   * Updates resource visibility and modifies links to include the selected search type.
   * @param {Event} event - The input event.
   */
  function onContentTypeChange(event) {
    const resultsContainer = event.target.closest(SELECTORS.resultsContainer);
    if (!resultsContainer) return;

    const selectedInput = resultsContainer.querySelector(SELECTORS.searchTypeChecked);
    if (!selectedInput) return;

    const searchType = selectedInput.value;

    const resources = Array.from(resultsContainer.querySelectorAll(SELECTORS.resources));

    resources.forEach(resource => {
      if (resource.dataset.predictiveSearchResources === searchType) {
        resource.removeAttribute('hidden');
      } else {
        resource.setAttribute('hidden', 'true');
      }
    });

    updateSearchTypeLinks(searchType, resultsContainer);
  }

  /**
   * Updates all predictive search links to include the currently selected search type as a URL parameter.
   * @param {string} searchType - The search type (e.g., 'product', 'article', etc.)
   * @param {HTMLElement} resultsContainer - The specific results container.
   */
  function updateSearchTypeLinks(searchType, resultsContainer) {
    const links = resultsContainer.querySelectorAll(SELECTORS.predictiveSearchLink);
    links.forEach(link => {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('type', searchType);
      link.href = url.toString();
    });
  }
})();
